import { createContext, useContext, type ReactNode } from "react";
import type { LandingCopy } from "@/data/landingParents";
import { useLanguage } from "@/lib/i18n";

/**
 * Shared configuration for a Meta-ads landing page. The landing components
 * (hero, sections, chrome, sticky CTA, SEO) are audience-agnostic — they read
 * their copy and paths from this context. Each landing page (parents, reno, …)
 * provides its own config so the SAME components can serve multiple campaigns.
 */
export interface LandingConfig {
  /** Localized copy for both languages. */
  copy: Record<"fr" | "en", LandingCopy>;
  /** Canonical FR path (leading slash, no trailing slash). */
  frPath: string;
  /** Canonical EN path (leading slash, no trailing slash). */
  enPath: string;
  /**
   * Optional hero photo path (e.g. "/reno-hero.jpg"). Leave null to fall back
   * to the app mockup — no broken image is ever shown.
   */
  heroImage: string | null;
  /** Analytics page id, e.g. "reno_returns". */
  analyticsPage: string;
  /** Apple custom product page id (ppid) appended to the App Store URL. */
  appStorePpid: string;
}

const LandingContext = createContext<LandingConfig | null>(null);

export function LandingProvider({
  config,
  children,
}: {
  config: LandingConfig;
  children: ReactNode;
}) {
  return <LandingContext.Provider value={config}>{children}</LandingContext.Provider>;
}

/** The full landing config (copy for both languages + paths). */
export function useLandingConfig(): LandingConfig {
  const config = useContext(LandingContext);
  if (!config) {
    throw new Error("useLandingConfig must be used within a <LandingProvider>");
  }
  return config;
}

/** The copy for the active language — the common case in section components. */
export function useLandingCopy(): LandingCopy {
  const { language } = useLanguage();
  return useLandingConfig().copy[language];
}
